import FooterStore from 'store/modules/Footer/Footer'
import store from 'definitions/store'
import CookieUtils from 'utils/CookieUtils'
import JwtUtils from 'utils/JwtUtils'
import axios from 'axios'
import Storyblok from '../storyblok'
import conf from 'conf'
import * as Sentry from '@sentry/vue'

const EMARSYS_PUSH_RETRY_AT_STORAGE_KEY = 'emarsys_push_retry_at'
const EMARSYS_PUSH_RETRY_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000
const EMARSYS_COOKIE_ACCEPT_EVENT_STORAGE_KEY = 'emarsys_cookie_accept_event'

Storyblok.init()

const Init = {
  async sendEmarsysCookieAcceptEvent() {
    const consent = store.state.main.gdprCookie || []
    const web = consent.includes('web')
    const adv = consent.includes('adv')

    if (!web && !adv) return

    const eventData = { web: String(web), adv: String(adv) }
    const serializedEventData = JSON.stringify(eventData)

    try {
      if (
        window.localStorage.getItem(EMARSYS_COOKIE_ACCEPT_EVENT_STORAGE_KEY) ===
        serializedEventData
      ) {
        return
      }

      if (await window.WebEmarsysSdk.customEvent('cookie_accept', eventData)) {
        window.localStorage.setItem(
          EMARSYS_COOKIE_ACCEPT_EVENT_STORAGE_KEY,
          serializedEventData
        )
      }
    } catch (error) {
      console.warn('[Emarsys] customEvent error', error)
    }
  },
  consentGrantedOnPage: false,
  hasSapConsent(consent) {
    return consent.includes('web') || consent.includes('adv')
  },
  getLoggedInEmail() {
    try {
      const jwt = JwtUtils.getJwtContentFromCookie(conf.jwtCookieName)
      return (jwt && jwt.email) || ''
    } catch (e) {
      return ''
    }
  },
  watchSapConsent() {
    let previousConsent = (store.state.main.gdprCookie || []).join(',')

    store.watch(
      (state) => state.main.gdprCookie,
      (consent = []) => {
        const currentConsent = consent.join(',')
        if (currentConsent === previousConsent) return
        previousConsent = currentConsent

        if (!Init.hasSapConsent(consent)) {
          Init.removeSAP()
          return
        }

        const wasSdkLoaded = Boolean(document.getElementById('emarsys-sdk'))
        Init.consentGrantedOnPage = true
        Init.InitSAP()
        if (wasSdkLoaded) {
          window.WebEmarsysSdk.push(Init.sendEmarsysCookieAcceptEvent)
        }
      }
    )
  },
  async removeSAP() {
    const sdk = window.WebEmarsysSdk
    if (sdk && !Array.isArray(sdk)) await sdk.unsubscribe()

    for (const id of ['emarsys-sdk', 'scarab-js-api', 'scarab-end']) {
      const element = document.getElementById(id)
      if (element) element.remove()
    }
    window.WebEmarsysSdk = []
    window.ScarabQueue = []
  },
  InitSAP() {
    try {
      const gdprCookies = store.state.main.gdprCookie

      if (!gdprCookies.includes('web') && !gdprCookies.includes('adv')) {
        console.log('SAP init skipped due to missing consent')
        return
      }

      Init.injectSAP()
      Init.injectSapSdk()
    } catch (e) {
      console.warn('SAP init check failed', e)
    }
  },
  injectSAP() {
    try {
      if (typeof document === 'undefined' || document.getElementById('scarab-js-api')) {
        console.log('SAP script already injected')
        return
      }

      window.ScarabQueue = window.ScarabQueue || []

      const email = Init.getLoggedInEmail()
      if (email) {
        window.ScarabQueue.push(['setEmail', email])
      }

      const js = document.createElement('script')
      js.id = 'scarab-js-api'
      js.src = `https://cdn.scarabresearch.com/js/${conf.sapID}/scarab-v2.js`
      const fs = document.getElementsByTagName('script')[0]
      fs && fs.parentNode && fs.parentNode.insertBefore(js, fs)

      const tag = document.createElement('script')
      tag.id = 'scarab-end'
      tag.type = 'text/javascript'
      tag.innerHTML = `
        if (window.ScarabQueue) {
          window.ScarabQueue.push(['go'])
        } else {
          window.ScarabQueue = [['go']]
        }
      `
      const position = document.querySelector('body')
      position.appendChild(tag)
    } catch (e) {
      console.error('Failed to inject SAP script', e)
    }
  },
  injectSapSdk() {
    try {
      if (typeof document === 'undefined' || document.getElementById('emarsys-sdk')) {
        console.log('Emarsys SDK script already injected')
        return
      }

      if (!conf.sapAppCode || !conf.sapPublicKey || !conf.sapDomain) {
        console.warn('SAP SDK config is incomplete, skipping SDK injection')
        return
      }

      const js = document.createElement('script')
      js.id = 'emarsys-sdk'
      js.src = `https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/latest/web-emarsys-sdk.js`
      const fs = document.getElementsByTagName('script')[0]
      fs && fs.parentNode && fs.parentNode.insertBefore(js, fs)
    } catch (e) {
      console.error('Failed to inject Emarsys sdk script', e)
    }

    window.WebEmarsysSdk = window.WebEmarsysSdk || []

    const config = {
      enableLogging: conf.sapDebug,
      applicationCode: conf.sapAppCode,
      enableMacSafariVapid: true,
      safariWebsitePushID: conf.sapDomain,
      defaultNotificationTitle: 'Where will you go next?',
      defaultNotificationIcon: `${conf.btAssets}/logo/airBaltic_logo_192x192.jpg`,
      autoSubscribe: false,
      serviceWorker: {
        url: 'service-worker.js',
        applicationServerPublicKey: conf.sapPublicKey
      }
    }

    window.WebEmarsysSdk.push(['init', config])
    window.WebEmarsysSdk.push(Init.onSdkReady)

    if (conf.sapDebug) {
      console.log('configuring Emarsys SDK with debug logging enabled', config)

      window.WebEmarsysSdk.push([
        'onSubscribe',
        (e) => {
          console.log('onSubscribe', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onUnsubscribe',
        (e) => {
          console.log('onUnsubscribe', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionPrompt',
        (e) => {
          console.log('onPermissionPrompt', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionGranted',
        (e) => {
          console.log('onPermissionGranted', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onPermissionDenied',
        (e) => {
          console.log('onPermissionDenied', e)
        }
      ])
      window.WebEmarsysSdk.push([
        'onSWInitError',
        (e) => {
          console.error('[Emarsys] onSWInitError', e)
        }
      ])
    }
  },
  reportPushIssue(message, extra = {}) {
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { integration: 'emarsys-web-sdk' },
      extra
    })
  },
  async shouldSubscribeToPush() {
    const { permission } = Notification
    if (permission === 'default') return true
    if (permission !== 'granted') return false
    if (Init.consentGrantedOnPage) return true

    const retryAt =
      Number(window.localStorage.getItem(EMARSYS_PUSH_RETRY_AT_STORAGE_KEY)) || 0
    return Date.now() >= retryAt && !(await window.WebEmarsysSdk.isSubscribed())
  },
  async onSdkReady() {
    await Init.subscribeToPush()
    await Init.sendEmarsysCookieAcceptEvent()
  },
  async subscribeToPush() {
    if (typeof Notification === 'undefined') return

    try {
      if (!(await Init.shouldSubscribeToPush())) return

      window.localStorage.setItem(
        EMARSYS_PUSH_RETRY_AT_STORAGE_KEY,
        String(Date.now() + EMARSYS_PUSH_RETRY_INTERVAL_MS)
      )
      await window.WebEmarsysSdk.subscribe()

      if (
        Notification.permission === 'granted' &&
        !(await window.WebEmarsysSdk.isSubscribed())
      ) {
        Init.reportPushIssue('emarsys_push_registration_incomplete')
      }
    } catch (error) {
      console.warn('[Emarsys] Push subscription failed', error)
      Init.reportPushIssue('emarsys_push_subscribe_failed', {
        errorName: error instanceof Error ? error.name : typeof error
      })
    }
  },
  init(name) {
    if (!store.hasModule(name)) {
      store.registerModule(name, FooterStore)
    }

    Init.initCookieAlert()
    Init.watchSapConsent()
    Init.initBaseFooter()

    if (conf.storyblokEnabled) {
      Init.initStoryblokFooterItems(name)
      Init.initStoryblokFooterFaceliftLinks(name)
    } else {
      Init.initFooter()
      Init.initFooterFaceliftLinks(name)
    }
  },
  initBaseFooter() {
    if (store.state.main.pageType === 'index') {
      store.commit('footer/setShowScrollButton', false)
    }

    if (typeof subscribePopupData !== 'undefined' && subscribePopupData.length) {
      store.commit('footer/setSubscribePopupData', subscribePopupData[0])
    }

    if (typeof disableSubscribePopup !== 'undefined') {
      store.commit(`footer/setDisableSubscribePopup`, disableSubscribePopup)
    }
  },
  async initStoryblokFooterItems(name) {
    try {
      const language = store.state.main.selectedLang
      const footerLinks = await Storyblok.footerNavigation(language)

      store.commit(`${name}/setFooterItems`, footerLinks)
    } catch (error) {
      console.error('main-navigation/footer-navigation could not be loaded', error)
    }
  },
  async initStoryblokFooterFaceliftLinks(name) {
    const language = store.state.main.selectedLang
    const country = store.state.main.selectedCountry

    try {
      const {
        social,
        footerSemantics,
        footerAwards,
        contactUs,
        legalLinks,
        copyright,
        googlePrivacyPolicy,
        machineTranslationDisclaimer,
        paymentLogos
      } = await Storyblok.mainFooter(language, country)

      store.commit(`${name}/setFooterContactUs`, contactUs)
      store.commit(`${name}/setFooterLegalLinks`, legalLinks)
      store.commit(`${name}/setSocial`, social)
      store.commit(`${name}/setCopyright`, copyright)
      store.commit(`${name}/setSemantics`, footerSemantics)
      store.commit(`${name}/setAwards`, footerAwards)
      store.commit(`${name}/setGooglePrivacyPolicyText`, googlePrivacyPolicy)
      store.commit(
        `${name}/setMachineTranslationDisclaimer`,
        machineTranslationDisclaimer
      )
      store.commit(`${name}/setPaymentLogos`, paymentLogos)
    } catch (error) {
      console.error('main-navigation/main-footer could not be loaded', error)
    }
  },
  initCookieAlert() {
    if (typeof cookieContent !== 'undefined') {
      store.commit('main/setCookieAlertContent', cookieContent)

      const value = CookieUtils.getCookie('gdpr_settings') || ''
      const split = value ? value.split(',') : []
      const hasValue = ['fnc', 'sts', 'web', 'adv', '0', '1', '2', '3'].some((item) =>
        split.includes(item)
      )

      const hideAlert = Boolean(value && hasValue)
      store.commit('main/setHideCookieAlert', hideAlert)

      if (hasValue) {
        const presetMap = {
          '0': ['fnc', 'web', 'adv'],
          '1': ['fnc', 'web'],
          '2': ['fnc', 'adv'],
          '3': ['fnc']
        }

        const matchedPreset = Object.keys(presetMap).find((key) => split.includes(key))
        const cookieValue = matchedPreset ? presetMap[matchedPreset] : split

        store.dispatch('main/setGdprCookie', cookieValue)
        CookieUtils.setCookie('2e_send_GA', cookieValue.includes('sts'), 365)

        Init.InitSAP()
      }
    }

    if (
      store.state.main.pageType === 'privacypolicy-white' ||
      store.state.main.pageType === 'cookie_policy'
    ) {
      store.commit('main/setHideCookieAlert', true)
    }
  },
  initFooter() {
    if (typeof googlePrivacyPolicyText !== 'undefined') {
      store.commit('footer/setGooglePrivacyPolicyText', googlePrivacyPolicyText)
    }

    if (typeof footerSemantics !== 'undefined') {
      store.commit('footer/setSemantics', footerSemantics)
    }
  },
  initFooterFaceliftLinks(name) {
    axios
      .get(`/get-wide-footer-facelift-links/${store.state.main.selectedLang}/`)
      .then((response) => {
        store.commit(`${name}/setFooterLinks`, response.data)
      })
      .catch((e) => console.error('Error fetching footer links', e))
  }
}

export default Init
